 "use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useSWR from "swr";
import { IoSearchSharp, IoCloseSharp } from "react-icons/io5";
import { Link } from "react-router-dom";
import { ASSET_URI, PROXY, limitCharecters } from "../lib/utils";
import { useTranslation } from "react-i18next";
import SmallSpinner from "./SmallSpinner";

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const LANGUAGES = ["en", "ps", "pr"];

const stripHtml = (value = "") => value.replace(/<[^>]*>/g, "");

const highlightMatch = (text = "", query = "") => {
  if (!query) {
    return text;
  }
  const escaped = escapeRegExp(query);
  const regex = new RegExp(`(${escaped})`, "gi");
  return text.split(regex).map((part, index) =>
    index % 2 === 1 ? (
      <mark
        key={`${part}-${index}`}
        className="rounded bg-indigo-100 px-0.5 text-indigo-800"
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const SearchModal = ({ openSearch, onClose }) => {
  const [search, setSearch] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [portalNode, setPortalNode] = useState(null);
  const searchRef = useRef(null);
  const scrollPosition = useRef(0);
  const { t, i18n } = useTranslation();
  const languageLabels = {
    en: t("language.english", "English"),
    ps: t("language.pashto", "Pashto"),
    pr: t("language.persian", "Persian"),
  };

  const closeHandler = useCallback(() => {
    setSearch("");
    setDebouncedQuery("");
    onClose();
  }, [onClose]);

  const clearSearch = useCallback(() => {
    setSearch("");
    setDebouncedQuery("");
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const node = document.createElement("div");
    node.setAttribute("id", "search-modal-portal");
    document.body.appendChild(node);
    setPortalNode(node);

    return () => {
      if (document.body.contains(node)) {
        document.body.removeChild(node);
      }
    };
  }, []);

  useEffect(() => {
    if (openSearch && portalNode) {
      searchRef.current?.focus();
    }
  }, [openSearch, portalNode]);

  useEffect(() => {
    if (!openSearch || typeof document === "undefined") return;
    const body = document.body;
    scrollPosition.current = window.scrollY || window.pageYOffset || 0;
    body.style.position = "fixed";
    body.style.top = `-${scrollPosition.current}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflowY = "hidden";

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflowY = "";
      if (typeof window.scrollTo === "function") {
        try {
          window.scrollTo(0, scrollPosition.current || 0);
        } catch {
          // Some test environments (jsdom) throw on scrollTo.
        }
      }
    };
  }, [openSearch]);

  useEffect(() => {
    if (!openSearch) {
      setDebouncedQuery("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedQuery(search);
    }, 300);

    return () => clearTimeout(handler);
  }, [search, openSearch]);

  useEffect(() => {
    if (!openSearch) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeHandler();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeHandler, openSearch]);

  const fetcher = (...args) => fetch(...args).then((res) => {
    if (!res.ok) throw new Error("Search failed");
    return res.json();
  });
  const normalizedQuery = debouncedQuery.trim();
  const { data, error, isLoading } = useSWR(
    normalizedQuery ? `${PROXY}/courses/search/g?q=${encodeURIComponent(normalizedQuery)}` : null,
    fetcher
  );

  const courseResults = data?.courses ?? [];
  const hasResults = courseResults.length > 0;
  const showNoResults = !isLoading && search.trim().length > 0 && !hasResults;
  const errorMessage = t("search-error", "Unable to load search results right now.");
  const statusLabel = search.trim()
    ? `${courseResults.length} ${t("results", "results")}`
    : t("start-typing", "Start typing to search for courses");

  if (!portalNode || !openSearch) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-3">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-modal-title"
        className="relative w-full max-w-3xl rounded-2xl border border-gray-200 bg-white shadow-2xl backdrop-blur-sm"
      >
        <div className="flex max-h-[90vh] flex-col overflow-hidden">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 id="search-modal-title" className="text-lg font-semibold text-gray-900">
              {t("search-for")}
            </h2>
            <div className="mt-3">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-indigo-600">
                  <IoSearchSharp className="h-5 w-5" aria-hidden="true" />
                </span>
                <input
                  id="search-modal-input"
                  role="searchbox"
                  aria-label={t("search-for")}
                  type="text"
                  maxLength={100}
                  value={search}
                  ref={searchRef}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("search-for")}
                  className="w-full rounded-md border border-indigo-500 bg-gray-50 py-3 pl-11 pr-12 text-sm text-gray-900 transition focus:border-indigo-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
                {search.length > 0 && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label={t("clear-search", "Clear search term")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <IoCloseSharp className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span className="flex items-center gap-2">
                {isLoading && (
                  <SmallSpinner />
                )}
                <span>{isLoading ? t("loading", "Loading…") : statusLabel}</span>
              </span>
              {error && (
                <span className="text-red-500" role="alert">
                  {errorMessage}
                </span>
              )}
            </div>
          </div>
            <div
              role="list"
              aria-live="polite"
              className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin scrollbar-thumb-indigo-300"
            >
            {hasResults && (
              <ul className="space-y-3">
                {courseResults.map((course) => {
                  const courseTranslations = LANGUAGES.map((lang) => ({
                    lang,
                    title: stripHtml(
                      course?.title?.[lang] ??
                        course?.title?.en ??
                        ""
                    ),
                    description: stripHtml(
                      course?.desc?.[lang] ?? course?.desc?.en ?? ""
                    ),
                  }));
                  const primaryLang = LANGUAGES.includes(i18n.language)
                    ? i18n.language
                    : LANGUAGES[0];
                  const primaryTranslation =
                    courseTranslations.find(
                      (entry) => entry.lang === primaryLang
                    ) ?? courseTranslations[0];
                  const secondaryTranslations = courseTranslations.filter(
                    (entry) => entry.lang !== primaryTranslation.lang
                  );
                  return (
                    <li key={course._id} role="listitem">
                      <Link
                        to={`/courses/${course._id}`}
                        onClick={closeHandler}
                        className="group flex gap-4 rounded-xl border border-gray-100 bg-gray-50 p-3 text-left transition hover:border-indigo-500 hover:bg-indigo-500 hover:text-white"
                      >
                        <img
                          src={`${ASSET_URI}/${course?.image}`}
                          alt={
                            primaryTranslation.title ||
                            t("course-card-image", "Course image")
                          }
                          className="h-12 w-12 rounded-full object-cover"
                          width={48}
                          height={48}
                          crossOrigin="anonymous"
                        />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold leading-tight text-gray-900 transition-colors duration-200 group-hover:text-white">
                            {highlightMatch(
                              limitCharecters(primaryTranslation.title, 36),
                              search
                            )}
                          </h3>
                          <p className="mt-1 text-xs text-gray-600 transition-colors duration-200 group-hover:text-indigo-50">
                            {highlightMatch(
                              limitCharecters(primaryTranslation.description, 85),
                              search
                            )}
                          </p>
                          <div className="mt-2 space-y-1 text-[11px] text-gray-500 transition-colors duration-200 group-hover:text-indigo-100">
                            {secondaryTranslations.map((translation) => (
                              <p
                                key={`${course._id}-${translation.lang}`}
                                className="flex flex-wrap items-center gap-1"
                              >
                                <span className="font-semibold text-indigo-600 transition-colors duration-200 group-hover:text-white">
                                  {languageLabels[translation.lang] ||
                                    translation.lang.toUpperCase()}
                                  :
                                </span>
                                <span>
                                  {highlightMatch(
                                    limitCharecters(translation.title, 30),
                                    search
                                  )}
                                </span>
                              </p>
                            ))}
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            {showNoResults && (
              <p className="mt-4 text-center text-sm text-gray-500">
                {t("no-search-data")}
              </p>
            )}
          </div>
          <div className="flex justify-between border-t border-gray-100 px-5 py-4">
            <Link to={`/courses?keyword=${encodeURIComponent(search.trim())}`} onClick={closeHandler} className="rounded-lg px-4 py-2 text-indigo-700 focus:ring-2 focus:ring-indigo-500">{t("courseSearch.allResults")}</Link>
            <button
              type="button"
              className="rounded-full border border-indigo-500 bg-indigo-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={closeHandler}
            >
              {t("close")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    portalNode
  );
};

export default SearchModal;
